// Protected Route Component
// Create this as frontend/src/components/ProtectedRoute.js

import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { authAPI, getUser, setUser, removeUser } from '../services/authApi';

/**
 * ProtectedRoute component that wraps routes requiring authentication
 * Redirects to login if user is not authenticated
 */
function ProtectedRoute({ children }) {
  const [checking, setChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    let mounted = true;

    const verifySession = async () => {
      const localUser = getUser();

      // If user exists locally, allow render immediately while we validate in background.
      if (localUser && mounted) {
        setIsAuthenticated(true);
        setChecking(false);
      }

      try {
        const serverUser = await authAPI.getCurrentUser();
        if (!mounted) return;
        setUser(serverUser);
        setIsAuthenticated(true);
      } catch {
        if (!mounted) return;
        removeUser();
        setIsAuthenticated(false);
      } finally {
        if (mounted) setChecking(false);
      }
    };

    verifySession();

    return () => {
      mounted = false;
    };
  }, []);

  if (checking) {
    return <div style={{ padding: '24px', textAlign: 'center' }}>Loading...</div>;
  }

  if (!isAuthenticated) {
    // Redirect to login page
    return <Navigate to="/login" replace />;
  }

  // User is authenticated, render the protected component
  return children;
}

export default ProtectedRoute;

// Usage example in App.js:
/*
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import CattleList from './pages/CattleList';

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        
        <Route path="/cattle" element={
          <ProtectedRoute>
            <CattleList />
          </ProtectedRoute>
        } />
        
        // ... other protected routes
      </Routes>
    </BrowserRouter>
  );
}

export default App;
*/
